import { Component, OnInit, AfterViewChecked, QueryList, ViewChildren, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BaseQuestionComponent } from '../base-question.component';
import { QuestionOption } from '@formrails/form-builder/data-access';

/**
 * Checkbox question component.
 * Displays and configures a checkbox list question.
 * Uses Angular signals inherited from BaseQuestionComponent.
 */
@Component({
  selector: 'app-checkbox-question',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './checkbox-question.component.html',
  styleUrl: './checkbox-question.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckboxQuestionComponent extends BaseQuestionComponent implements OnInit, AfterViewChecked {
  @ViewChildren('optionInput') optionInputs!: QueryList<ElementRef<HTMLInputElement>>;

  options: QuestionOption[] = [];
  selectedOptionIndices: Set<number> = new Set();
  private newOptionIndex: number | null = null;

  ngOnInit(): void {
    const q = this.question();
    // Initialize options from question model or start with one empty option
    if (q.options && q.options.length > 0) {
      // Convert string[] to QuestionOption[] if needed (backward compatibility)
      if (typeof q.options[0] === 'string') {
        this.options = (q.options as string[]).map(text => ({
          text,
          isCustomOption: false
        }));
      } else {
        this.options = (q.options as QuestionOption[]).map(opt => ({
          text: opt.text,
          isCustomOption: opt.isCustomOption || false,
          customValue: opt.customValue
        }));
      }
    } else {
      this.options = [{ text: '', isCustomOption: false }];
    }

    // Initialize custom option values from question model
    // Create a new object if customOptionValues doesn't exist (question may be frozen)
    if (!q.customOptionValues) {
      this.emitQuestionChange({ ...q, customOptionValues: {} });
    }
  }

  onLabelChange(value: string): void {
    this.updateLabel(value);
  }

  onHelperTextChange(value: string): void {
    this.updateHelperText(value);
  }

  onAdditionalInfoChange(value: string): void {
    this.updateAdditionalInfo(value);
  }

  // Dynamic options toggle and configuration methods
  onDynamicOptionsToggle(enabled: boolean): void {
    this.emitQuestionChange({ ...this.question(), hasDynamicOptions: enabled });
  }

  onSourceContainerChange(value: string): void {
    this.emitQuestionChange({ ...this.question(), sourceContainer: value });
  }

  onTextPropertyChange(value: string): void {
    this.emitQuestionChange({ ...this.question(), textProperty: value });
  }

  onCustomOptionIdChange(value: string): void {
    this.emitQuestionChange({ ...this.question(), customOptionId: value });
  }

  onCustomOptionTextChange(value: string): void {
    this.emitQuestionChange({ ...this.question(), customOptionText: value });
  }

  /**
   * Check if the checkbox has populated options (either static or dynamic)
   */
  hasPopulatedOptions(): boolean {
    const q = this.question();
    return !!(q.options && q.options.length > 0 && q.options.some(opt => {
      const text = typeof opt === 'string' ? opt : opt.text;
      return text && text.trim() !== '';
    }));
  }

  onOptionChange(index: number, value: string): void {
    // Update local array and persist immediately so typed text isn't lost if
    // the user clicks Accept without blurring the input.
    this.options[index] = { ...this.options[index], text: value };
    this.saveOptions();
  }

  onOptionBlur(): void {
    // Kept for symmetry with radio-question; ensures a save happens even if a
    // downstream consumer suppressed the input events.
    this.saveOptions();
  }

  onCustomOptionToggle(index: number, enabled: boolean): void {
    this.options[index] = { ...this.options[index], isCustomOption: enabled };
    this.saveOptions();
  }

  onCustomValueChange(index: number, value: string): void {
    this.options[index] = { ...this.options[index], customValue: value };
    const q = this.question();
    // Create a new customOptionValues object (question may be frozen)
    const currentCustomValues = q.customOptionValues || {};
    const updatedCustomValues = { ...currentCustomValues, [index]: value };
    this.emitQuestionChange({ ...q, customOptionValues: updatedCustomValues });
  }

  onCheckboxToggle(index: number, checked: boolean): void {
    if (checked) {
      this.selectedOptionIndices.add(index);
    } else {
      this.selectedOptionIndices.delete(index);
    }
  }

  isOptionSelected(index: number): boolean {
    return this.selectedOptionIndices.has(index);
  }

  /**
   * Ticked options that take a typed-in value. Their free-text boxes render
   * under the options list rather than beside the tick they belong to, so the
   * template needs them collected in option order.
   */
  selectedCustomOptionIndices(): number[] {
    return this.options
      .map((option, index) => ({ option, index }))
      .filter(({ option, index }) => option.isCustomOption && this.isOptionSelected(index))
      .map(({ index }) => index);
  }

  trackByIndex(index: number): number {
    return index;
  }

  addOption(): void {
    const newIndex = this.options.length;
    this.options.push({ text: '', isCustomOption: false });
    this.newOptionIndex = newIndex;
    this.saveOptions();
  }
  
  ngAfterViewChecked(): void {
    if (this.newOptionIndex !== null && this.optionInputs && this.optionInputs.length > this.newOptionIndex) {
      const inputElement = this.optionInputs.toArray()[this.newOptionIndex]?.nativeElement;
      if (inputElement) {
        setTimeout(() => {
          inputElement.focus();
          this.newOptionIndex = null;
        }, 0);
      }
    }
  }

  removeOption(index: number): void {
    if (this.options.length > 1) {
      this.options.splice(index, 1);
      this.selectedOptionIndices.delete(index);
      const q = this.question();
      // Update custom option values indices
      // Create a new object (question may be frozen)
      if (q.customOptionValues) {
        const newCustomValues: Record<number, string> = {};
        Object.keys(q.customOptionValues).forEach(key => {
          const keyNum = parseInt(key, 10);
          if (keyNum < index) {
            newCustomValues[keyNum] = q.customOptionValues![keyNum];
          } else if (keyNum > index) {
            newCustomValues[keyNum - 1] = q.customOptionValues![keyNum];
          }
        });
        this.emitQuestionChange({ ...q, customOptionValues: newCustomValues });
      }
      this.saveOptions();
    }
  }

  getOptionsForDisplay(): (string | QuestionOption)[] {
    const q = this.question();
    if (!q.options || q.options.length === 0) {
      return [];
    }
    return q.options;
  }

  getOptionText(option: string | QuestionOption): string {
    if (typeof option === 'string') {
      return option;
    }
    return option.text;
  }

  private saveOptions(): void {
    // Preserve the full options list (including empty rows) so newly-added or
    // partially-typed entries survive between edits. Empty entries are only
    // pruned at final-form-submit time by the consumer.
    const optionsToSave = this.options.length > 0
      ? [...this.options]
      : [{ text: '', isCustomOption: false }];
    this.emitConfigChange({ options: optionsToSave } as any);
    this.emitQuestionChange({ ...this.question(), options: optionsToSave });
  }

  onRequiredToggle(): void {
    this.updateRequired(!this.question().isRequired);
  }
}

